import type { LocalizedDict } from '../../i18n'
const en = {
  title: 'Report a problem',
  intro: 'Tell us what happened; follow the resolution here.',
  newReport: 'New report',
  myReports: 'My reports',
  admin: 'Review reports',
  notifications: 'Updates',
  kind: 'What would you like to share?',
  bug: 'I have a problem',
  improvement: 'I have a suggestion',
  description: 'What happened?',
  descriptionHelp: 'What were you trying to do? What did you expect?',
  descriptionMinimum: 'At least 10 characters',
  placeholder: 'For example, while buying a shipping label…',
  images: 'Add images or video',
  optional: 'Optional',
  notSpecified: 'Not specified',
  choose: 'Choose files',
  capture: 'Capture this screen',
  record: 'Record a browser tab',
  stopRecording: 'Stop recording',
  recordingUnavailable: 'Screen recording is unavailable in this browser. You can still add a screenshot.',
  recordHint: 'Chrome: choose Chrome Tab → PEXIFY → Allow/Share. Window or Entire Screen will not work. Silent video stops after 59 seconds.',
  recordingFailed: 'Recording could not be completed. Try again or add a screenshot.',
  recordingWrongSource: 'Only a browser tab can be recorded. In Chrome, choose Chrome Tab → the PEXIFY tab.',
  recordingPermission: 'Tab sharing was not allowed. Choose the PEXIFY tab and allow sharing to record.',
  recordingDimensions: 'This tab’s video size could not be verified. Resize the browser window and try again.',
  recordingFormat: 'This browser cannot record a supported video format. You can still add a screenshot.',
  videoPrivacy: 'Review the video before sending. If it shows private information, remove it and record again. Video is not automatically masked.',
  videoReview: 'I reviewed this video and it contains no private information',
  video: 'Report video',
  removeVideo: 'Remove video',
  videoFailed: 'The video could not be prepared. Record a silent tab under 60 seconds and 20 MB.',
  imageHelp:
    'PNG, JPEG or WebP · Up to 8 MB each. You can also paste images here.',
  privacy:
    'Masking covers defined fields only. Other text may contain private information. Review every image and cover sensitive areas before sending.',
  impact: 'How does this affect your work?',
  blocking: 'I cannot continue',
  difficult: 'It works, but is wrong or difficult',
  minor: 'A minor or visual issue',
  clearImpact: 'Clear impact',
  context: 'What information is included?',
  contextHelp:
    'Page, application version and limited error information. Passwords, form values and request content are not collected. Check private information in images yourself.',
  send: 'Send report',
  sending: 'Saving report…',
  saved: 'Report saved',
  view: 'View report',
  retry: 'Try again',
  recover: 'Check saved result',
  pending:
    'The result is not yet known. Keep this report and check again; a retry uses the same request.',
  unavailable:
    'New reports are unavailable. You can still read your reports and check an earlier result.',
  invalid: 'Enter 10–5,000 characters.',
  error: 'This action could not be completed. Try again.',
  quota: 'The report limit has been reached. Try again later.',
  wait: 'Seconds before retry:',
  captureFailed:
    'This screen could not be captured. Describe the issue or choose an image instead.',
  imageFailed:
    'The image could not be prepared. Use a PNG, JPEG or WebP under 8 MB.',
  review: 'I reviewed this image and covered private information',
  remove: 'Remove image',
  redact: 'Cover selected area permanently',
  rectangle: 'Select an area by dragging, or enter pixel coordinates.',
  x: 'Left',
  y: 'Top',
  width: 'Width',
  height: 'Height',
  image: 'Report image',
  staged: 'Ready for review',
  uploading: 'Uploading',
  reserving: 'Preparing upload',
  confirming: 'Checking attachment',
  failed: 'Image failed',
  attached: 'Attached',
  summary: 'Images: attached / failed / pending',
  mediaSummary: 'Attachments: attached / failed / pending',
  rejected:
    'This image was rejected. You can discard this completed form and start another report.',
  close: 'Close',
  return: 'Return to report',
  discard: 'Discard draft',
  closeDraft: 'Discard this draft?',
  closePending:
    'The result is still being checked. Closing cannot cancel an accepted report. Return to check the original request.',
  keepClose: 'Keep and close',
  nextReport: 'Start another report',
  loading: 'Loading…',
  empty: 'No reports yet',
  emptyHelp: 'Your reports and resolution updates will appear here.',
  next: 'Next page',
  back: 'Back',
  submitted: 'Submitted',
  updated: 'Updated',
  resolution: 'Resolution',
  release: 'Release',
  history: 'History',
  confirm: 'It is fixed',
  reopen: 'Still broken',
  explanation: 'What is still broken?',
  conflict:
    'This report changed. The latest version is shown; your explanation is preserved. Review it before sending again.',
  confirmed: 'Your response was saved.',
  unavailableTarget:
    'This report is unavailable in your current account context.',
  refreshImage: 'Refresh attachment',
  openOwn: 'Open my report',
  openAdmin: 'Open in review',
  noUpdates: 'No report updates yet',
  received: 'Received',
  investigating: 'Investigating',
  planned: 'Planned',
  fixed: 'Fixed',
  closed: 'Closed',
  created: 'Report submitted',
  reopened: 'Reopened',
  status_changed: 'Status updated',
  reporter_confirmed: 'Fix confirmed',
  confirmed_fixed: 'Fix confirmed',
  expected_behavior: 'Expected behavior',
  not_planned: 'Not planned',
  cannot_reproduce: 'Could not reproduce',
  out_of_scope: 'Out of scope',
  attachment_added: 'Image attached',
  attachment_added_neutral: 'Attachment added',
}
export const reportCopy: LocalizedDict<typeof en> = {
  en,
  tr: {
    title: 'Sorun bildir',
    intro: 'Ne olduğunu anlat; çözümü buradan takip et.',
    newReport: 'Yeni bildirim',
    myReports: 'Bildirimlerim',
    admin: 'Bildirimleri incele',
    notifications: 'Güncellemeler',
    kind: 'Ne paylaşmak istiyorsun?',
    bug: 'Bir sorun yaşıyorum',
    improvement: 'Bir önerim var',
    description: 'Ne oldu?',
    descriptionHelp: 'Ne yapmaya çalışıyordun? Ne olmasını bekliyordun?',
    descriptionMinimum: 'En az 10 karakter',
    placeholder: 'Örneğin, kargo etiketi almaya çalışırken…',
    images: 'Görsel veya video ekle',
    optional: 'İsteğe bağlı',
    notSpecified: 'Belirtilmedi',
    choose: 'Dosya seç',
    capture: 'Bu ekranı yakala',
    record: 'Tarayıcı sekmesini kaydet',
    stopRecording: 'Kaydı durdur',
    recordingUnavailable: 'Bu tarayıcıda ekran kaydı kullanılamıyor. Yine de ekran görüntüsü ekleyebilirsin.',
    recordHint: 'Chrome: Chrome Tab → PEXIFY → İzin ver/Paylaş. Pencere veya tüm ekran çalışmaz. Sessiz kayıt 59 saniyede durur.',
    recordingFailed: 'Kayıt tamamlanamadı. Yeniden dene veya ekran görüntüsü ekle.',
    recordingWrongSource: 'Yalnızca tarayıcı sekmesi kaydedilebilir. Chrome’da Chrome Tab → PEXIFY sekmesini seç.',
    recordingPermission: 'Sekme paylaşımına izin verilmedi. Kaydetmek için PEXIFY sekmesini seçip paylaşımı onayla.',
    recordingDimensions: 'Sekmenin video boyutu doğrulanamadı. Tarayıcı penceresini yeniden boyutlandırıp tekrar dene.',
    recordingFormat: 'Bu tarayıcı desteklenen video biçiminde kayıt yapamıyor. Ekran görüntüsü ekleyebilirsin.',
    videoPrivacy: 'Göndermeden önce videoyu incele. Özel bilgi görünüyorsa kaldırıp yeniden kaydet. Videoda otomatik maskeleme yoktur.',
    videoReview: 'Bu videoyu inceledim; özel bilgi içermiyor',
    video: 'Bildirim videosu',
    removeVideo: 'Videoyu kaldır',
    videoFailed: 'Video hazırlanamadı. 60 saniye ve 20 MB altında sessiz sekme kaydı yap.',
    imageHelp:
      'PNG, JPEG veya WebP · Her biri en fazla 8 MB. Buraya görsel de yapıştırabilirsin.',
    privacy:
      'Otomatik kapatma yalnız belirli alanları kapsar. Diğer metinler özel bilgi içerebilir. Göndermeden önce her görseli kontrol edip özel bilgileri kapat.',
    impact: 'İşini ne kadar etkiliyor?',
    blocking: 'İşime devam edemiyorum',
    difficult: 'Çalışıyor ama yanlış veya zor',
    minor: 'Küçük ya da görsel bir sorun',
    clearImpact: 'Etki seçimini kaldır',
    context: 'Bildirime hangi bilgiler eklenir?',
    contextHelp:
      'Sayfa, uygulama sürümü ve sınırlı hata bilgileri. Parolalar, form değerleri ve istek içerikleri toplanmaz. Görsellerdeki özel bilgileri kendin kontrol etmelisin.',
    send: 'Bildirimi gönder',
    sending: 'Bildirim kaydediliyor…',
    saved: 'Bildirim kaydedildi',
    view: 'Bildirimi görüntüle',
    retry: 'Tekrar dene',
    recover: 'Kayıt sonucunu kontrol et',
    pending:
      'Sonuç henüz bilinmiyor. Bu bildirimi koruyup tekrar kontrol et; aynı istek yeniden denenecek.',
    unavailable:
      'Yeni bildirimler şu an kapalı. Geçmişini okuyabilir ve önceki gönderimin sonucunu kontrol edebilirsin.',
    invalid: '10–5.000 karakter gir.',
    error: 'İşlem tamamlanamadı. Tekrar dene.',
    quota: 'Bildirim sınırına ulaşıldı. Daha sonra tekrar dene.',
    wait: 'Tekrar denemeden önce beklenecek saniye:',
    captureFailed:
      'Bu ekran yakalanamadı. Sorunu yazabilir veya dosyadan görsel seçebilirsin.',
    imageFailed:
      'Görsel hazırlanamadı. 8 MB altında PNG, JPEG veya WebP kullan.',
    review: 'Bu görseli kontrol ettim ve özel bilgileri kapattım',
    remove: 'Görseli kaldır',
    redact: 'Seçili alanı kalıcı olarak kapat',
    rectangle: 'Sürükleyerek alan seç veya piksel konumlarını gir.',
    x: 'Sol',
    y: 'Üst',
    width: 'Genişlik',
    height: 'Yükseklik',
    image: 'Bildirim görseli',
    staged: 'Kontrole hazır',
    uploading: 'Yükleniyor',
    reserving: 'Yükleme hazırlanıyor',
    confirming: 'Ek kontrol ediliyor',
    failed: 'Görsel yüklenemedi',
    attached: 'Eklendi',
    summary: 'Görseller: eklenen / başarısız / bekleyen',
    mediaSummary: 'Ekler: eklenen / başarısız / bekleyen',
    rejected:
      'Bu görsel reddedildi. Tamamlanmış formu kaldırıp başka bir bildirim başlatabilirsin.',
    close: 'Kapat',
    return: 'Bildirime dön',
    discard: 'Taslağı sil',
    closeDraft: 'Bu taslak silinsin mi?',
    closePending:
      'Sonuç hâlâ kontrol ediliyor. Kapatmak kabul edilmiş bildirimi iptal etmez. Aynı isteği kontrol etmek için geri dön.',
    keepClose: 'Koru ve kapat',
    nextReport: 'Başka bildirim başlat',
    loading: 'Yükleniyor…',
    empty: 'Henüz bir bildirimin yok',
    emptyHelp: 'Gönderdiğin sorunları ve çözüm durumlarını burada göreceksin.',
    next: 'Sonraki sayfa',
    back: 'Geri',
    submitted: 'Gönderilme',
    updated: 'Güncellenme',
    resolution: 'Çözüm',
    release: 'Sürüm',
    history: 'Geçmiş',
    confirm: 'Sorun çözüldü',
    reopen: 'Hâlâ sorun yaşıyorum',
    explanation: 'Hangi sorun devam ediyor?',
    conflict:
      'Bu bildirim değişti. Son sürüm gösteriliyor; açıklaman korundu. Tekrar göndermeden önce kontrol et.',
    confirmed: 'Yanıtın kaydedildi.',
    unavailableTarget: 'Bu bildirim mevcut hesap bağlamında görüntülenemiyor.',
    refreshImage: 'Eki yenile',
    openOwn: 'Bildirimimi aç',
    openAdmin: 'İnceleme ekranında aç',
    noUpdates: 'Henüz bildirim güncellemesi yok',
    received: 'Alındı',
    investigating: 'İnceleniyor',
    planned: 'Planlandı',
    fixed: 'Düzeltildi',
    closed: 'Kapandı',
    created: 'Bildirim gönderildi',
    reopened: 'Yeniden açıldı',
    status_changed: 'Durum güncellendi',
    reporter_confirmed: 'Çözüm doğrulandı',
    confirmed_fixed: 'Çözüm doğrulandı',
    expected_behavior: 'Beklenen davranış',
    not_planned: 'Planlanmıyor',
    cannot_reproduce: 'Tekrarlanamadı',
    out_of_scope: 'Kapsam dışı',
    attachment_added: 'Görsel eklendi',
    attachment_added_neutral: 'Ek eklendi',
  },
}

export function reportEventLabel(
  event: { event_type: string; to_status?: string | null },
  copy: typeof en,
  includesVideo = false,
): string {
  if (event.event_type === 'attachment_added') return includesVideo ? copy.attachment_added_neutral : copy.attachment_added
  return event.to_status && event.to_status in copy
    ? copy[event.to_status as keyof typeof copy] : copy.status_changed
}

export function reportClosureLabel(reason: string, copy: typeof en): string {
  switch (reason) {
    case 'confirmed_fixed':
    case 'expected_behavior':
    case 'not_planned':
    case 'cannot_reproduce':
    case 'out_of_scope':
      return copy[reason]
    default:
      return copy.closed
  }
}
